import type { SpokenWord } from "../provider.js";
import type { ShowreelScene } from "../scene.js";
import type { Figure, SaidMs } from "./types.js";

/**
 * What the voice says over a showreel piece in the Showreel style: its lines, word for word, in the order the piece
 * shows them. The model wrote what the voice says and what the screen shows as two texts, and the owner's first
 * Showreel video with a voice said one sentence over three lines that said something else (dev box, 2026-09-28). So
 * the worker makes a piece's spoken line from its lines once the page check has had them (`writeScript`), and again
 * when the owner changes them in the editor, and the voice can say nothing the screen does not.
 */

/** One thing the voice says: a line of the piece, by its place in `lines`, or the figure. */
export type SpokenUnit = { unit: { line: number } | "figure"; text: string };

/** A line with nothing to say in it: asShowreel's "…" for a piece left with no words. */
const WORDS = /[\p{L}\p{N}]/u;
/** A line that already ends a sentence, a closing quote or bracket after its stop allowed. */
const STOPPED = /[.!?…]["'”’)]*$/;
/**
 * A web address, an email or a phone number at the end of a line: a voice reads a stop after one as part of it, and a
 * close's address went to it as "….co.uk." (review, 2026-09-28). A price or a year is not one.
 */
const ADDRESS = /(?:[\p{L}\p{N}-]+\.)+\p{L}{2,}(?:\/\S*)?$|\+?\(?\d(?:[\s().-]*\d){6,}$/u;
const stopped = (t: string) => {
  if (STOPPED.test(t)) return t;
  const bare = t.replace(/[\s,;:–—-]+$/, "");
  return ADDRESS.test(bare) ? bare : `${bare}.`;
};

/**
 * A piece's spoken line and the units it is made of, in reading order. The figure is its own unit, where the piece
 * draws it (`figureText`): after the line above when there are two lines or more, else before the caption. A hero's
 * and a figure piece's lines are one sentence broken across lines, joined with a space and stopped at the end; a
 * reveal's, steps', flip's, photo piece's (one for each framing of its photo, 2026-09-29) and close's are items, each
 * stopped, so the voice does not run them on as one sentence. A line with no words in it is not said, and nor is a
 * close's address (its last line of two or three, as `closeParts` reads them): it stays on screen, unsaid, since a
 * voice reads a phone number or a web address slowly and it is heard badly. A Showreel of 15 seconds ran 22.4, 5.3 of
 * them the voice reading "06 10 66 29 00" (dev box, 2026-09-29). The text is the units joined with a space, so the
 * voice's words can be counted back to the line each one belongs to.
 */
export function showreelSpoken(scene: Pick<ShowreelScene, "piece" | "lines" | "figure">): { text: string; units: SpokenUnit[] } {
  const lines: SpokenUnit[] = scene.lines.map((l, i) => ({ unit: { line: i }, text: l.replace(/\s+/g, " ").trim() })).filter((u) => u.text).slice(0, 3);
  if (scene.piece === "close" && lines.length >= 2) lines.pop();
  if (scene.piece === "figure" && scene.figure) lines.splice(lines.length >= 2 ? 1 : 0, 0, { unit: "figure", text: sayFigure(scene.figure) });
  const said = lines.filter((u) => WORDS.test(u.text));
  const sentence = scene.piece === "hero" || scene.piece === "figure";
  const units = said.map((u, i) => (!sentence || i === said.length - 1 ? { ...u, text: stopped(u.text) } : u));
  return { text: units.map((u) => u.text).join(" "), units };
}

/** A word as the voice's timestamps and the lines are compared: lower case, letters and digits only. */
const bare = (w: string) => w.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
/** The first run of letters and digits in a word, lower case: "example.com." is found as "example", however it is said. */
const head = (w: string) => /[\p{L}\p{N}]+/u.exec(w.toLowerCase())?.[0] ?? "";
const LETTER = /\p{L}/u;
/** How many words on a unit's own words are looked through for the next one, past any the voice said its own way. */
const NEAR = 4;

/**
 * When the voice says each thing a showreel piece shows (`showreelSpoken`), from the words its timestamps give, in ms
 * from the start of the clip: when it starts each line (null for a line it does not say) and the figure, and when its
 * last word ends. Composing puts them on the shot's voice, and the piece lands each line on its word (`sayOn`).
 *
 * The timestamps follow the text the voice was given, so the words are usually the units' own, one for one, and each
 * unit starts on its first word. A voice that says a figure its own way ("30%" as "thirty percent"), and the align
 * fallback's transcript, give other words: then each unit is found by its first word with letters in it, in order,
 * and a unit that starts with a figure starts on the word after the one before it. When a unit's first word is not
 * there to be found, the words are not the lines (another style's dialogue, a transcript gone wrong), and there are
 * no times: the piece keeps its own grid rather than land a line on the wrong word.
 */
export function saidTimes(scene: Pick<ShowreelScene, "piece" | "lines" | "figure">, words: ReadonlyArray<Pick<SpokenWord, "text" | "startMs" | "endMs">>): SaidMs | null {
  const { units } = showreelSpoken(scene);
  if (!units.length || !words.length) return null;
  const tokens = units.map((u) => u.text.split(" "));
  const starts: number[] = [];
  if (tokens.flat().length === words.length && tokens.flat().every((t, i) => bare(t) === bare(words[i]!.text))) {
    let at = 0;
    for (const t of tokens) { starts.push(at); at += t.length; }
  } else {
    // Each unit's words in turn, from the word after the last one found: its first one must be there; the rest are
    // looked for a few words on, and a word the voice said its own way is passed over.
    let next = 0;
    for (const t of tokens) {
      if (LETTER.test(t[0]!)) {
        const at = words.findIndex((w, i) => i >= next && head(w.text) === head(t[0]!));
        if (at < 0) return null;
        starts.push(at);
        next = at + 1;
      } else {
        if (next >= words.length) return null;
        starts.push(next);
      }
      for (const w of t.slice(1)) {
        if (!LETTER.test(w)) continue;
        const at = words.findIndex((x, i) => i >= next && i < next + NEAR && head(x.text) === head(w));
        if (at >= 0) next = at + 1;
      }
    }
    // A figure the voice left out has no word of its own: the word it would start on is the next unit's.
    if (starts.some((s, i) => i > 0 && s <= starts[i - 1]!)) return null;
  }
  const said: SaidMs = { lines: scene.lines.map(() => null), endMs: words.at(-1)!.endMs };
  units.forEach((u, i) => {
    const ms = words[starts[i]!]!.startMs;
    if (u.unit === "figure") said.figure = ms;
    else said.lines[u.unit.line] = ms;
  });
  return said;
}

const LONG: Record<string, string> = { k: " thousand", K: " thousand", M: " million", B: " billion", bn: " billion" };

/**
 * A figure as the voice should read it: the value with its thousands grouped from four digits (1284 read as a year is
 * "twelve eighty-four"), a short form said long ("£1M" is "£1 million", which the voice would read as "one M"), and a
 * plus after the number said as "over" in front of it ("£1M+" is "over £1 million"). A sign and "%" stay as they are:
 * the voice reads those itself. A price keeps both digits of its pence, as it is drawn (`formatValue`): "£3.2" is not
 * how £3.20 is said (2026-09-29).
 */
export function sayFigure(f: Figure): string {
  const short = /^\s*(bn|[KkMB])(?![a-z])/.exec(f.suffix);
  let rest = short ? f.suffix.slice(short[0].length) : f.suffix;
  const over = /^\s*\+/.test(rest) || /\+\s*$/.test(rest);
  rest = rest.replace(/^\s*\+/, "").replace(/\+\s*$/, "");
  const prefix = f.prefix?.trim() ?? "";
  const [whole = "0", part] = String(Math.abs(f.value)).split(".");
  const grouped = whole.length >= 4 ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : whole;
  const pence = part && !short && /^[£$€]$/.test(prefix) ? part.padEnd(2, "0") : part;
  const said = `${over ? "over " : ""}${f.value < 0 ? "-" : ""}${prefix}${grouped}${pence ? `.${pence}` : ""}${short ? LONG[short[1]!] : ""}${rest}`;
  return said.replace(/\s+/g, " ").trim();
}
